import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Check, CheckCircle2, ExternalLink, Flag, Pause, Play, Send, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { checkAnswer, defaultParts, LETTER_POS_LABELS, letterIndexAt, LetterPos, wordLetters } from '../../../utils/initials';
import { Answer, currentRound, GameAction, GameState, otherSide, sideName } from '../../../utils/initialsGame';
import { Btn, Field, inputClass, LetterPair, Panel, PanelTitle, Segmented } from './parts';

/** One word with the letter the rule looks at highlighted. */
const WordChip: React.FC<{ word: string; pos: LetterPos | null; ok: boolean; role?: string }> = ({ word, pos, ok, role }) => {
  const letters = wordLetters(word);
  const hit = pos ? letterIndexAt(word, pos) : -1;
  return (
    <span className={cn('inline-flex flex-col items-center gap-1 rounded-xl border px-3 py-2', pos ? 'border-white/20 bg-white/10' : 'border-white/5 bg-white/[0.03] opacity-60')}>
      <span className="text-lg font-bold tracking-wide">
        {letters.map((l, i) => (
          <span key={i} className={cn(i === hit && (ok ? 'rounded bg-emerald-400 px-0.5 text-gray-900' : 'rounded bg-rose-500 px-0.5 text-white'))}>
            {l}
          </span>
        ))}
      </span>
      {role && <span className="text-[11px] font-semibold uppercase tracking-wider text-white/40">{role}</span>}
    </span>
  );
};

const searchLinks = (answer: Answer) => {
  const q = encodeURIComponent((answer.original || answer.fullName).trim());
  return [
    { label: 'Google-da axtar', href: `https://www.google.com/search?q=${q}` },
    { label: 'Vikipediyada axtar', href: `https://az.wikipedia.org/w/index.php?search=${q}` },
  ];
};

const formatSeconds = (ms: number) => Math.ceil(ms / 1000);

interface Props {
  state: GameState;
  dispatch: (action: GameAction) => void;
  remainingMs: number;
  running: boolean;
  usedKeys: string[];
}

const InitialsTurn: React.FC<Props> = ({ state, dispatch, remainingMs, running, usedKeys }) => {
  const round = currentRound(state)!;
  const { mode } = state.settings;
  const active = state.turn;
  const opponent = otherSide(active);

  const [fullName, setFullName] = useState('');
  const [original, setOriginal] = useState('');
  const [description, setDescription] = useState('');
  const [parts, setParts] = useState<{ nameIdx: number; surnameIdx: number } | null>(null);
  const [reason, setReason] = useState('');

  // Every turn starts with an empty form; a disputed answer is kept so it can be corrected.
  const turnKey = `${round.no}-${round.events.filter(e => e.kind !== 'rejected').length}`;
  useEffect(() => {
    setFullName('');
    setOriginal('');
    setDescription('');
    setParts(null);
  }, [turnKey]);
  useEffect(() => setReason(''), [state.pending]);

  const words = useMemo(() => fullName.trim().split(/\s+/).filter(w => wordLetters(w).length > 0), [fullName]);
  const effective = parts && parts.nameIdx < words.length && parts.surnameIdx < words.length ? parts : defaultParts(words);

  const result = checkAnswer({
    fullName,
    nameIdx: effective.nameIdx,
    surnameIdx: effective.surnameIdx,
    description,
    rule: round.rule,
    letters: round.letters,
    mode,
    usedKeys,
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!result.ok || !running) return;
    dispatch({
      type: 'submit',
      answer: {
        id: `${Date.now()}`,
        side: active,
        fullName: fullName.trim().replace(/\s+/g, ' '),
        original: original.trim(),
        description: description.trim(),
        ...effective,
      },
      now: Date.now(),
    });
  };

  const seconds = formatSeconds(remainingMs);
  const fraction = Math.min(1, remainingMs / (state.settings.turnSeconds * 1000));
  const urgent = seconds <= 10;
  // The opponent just failed: one accepted answer now wins the round.
  const lastDecisive = [...round.events].reverse().find(e => e.kind !== 'rejected');
  const decisive = lastDecisive?.kind === 'failed' && lastDecisive.side === opponent;
  const wordOptions = words.map((w, i) => ({ value: i, label: w }));

  return (
    <div className="flex flex-col gap-5">
      {/* Round letters + timer */}
      <Panel className="flex flex-col items-center gap-3 px-4 py-5 text-center">
        <LetterPair name={round.letters.name} surname={round.letters.surname} className="text-5xl sm:text-6xl" />
        <p className="text-sm text-white/60">
          Adın {LETTER_POS_LABELS[round.rule.name]}, soyadın {LETTER_POS_LABELS[round.rule.surname]} hərfi
        </p>
        <div className="mt-1 text-lg">
          Növbə: <span className="font-black text-sky-300">{sideName(state, active)}</span>
        </div>
        {decisive && state.phase === 'turn' && (
          <p className="rounded-lg bg-amber-400/15 px-3 py-1 text-sm font-semibold text-amber-200">
            Rəqib tapa bilmədi — bir şəxs tapsanız raund sizindir
          </p>
        )}
        <div className={cn('text-6xl font-black tabular-nums sm:text-7xl', urgent && state.phase === 'turn' ? 'text-rose-400' : 'text-white')}>
          {state.phase === 'review' ? <Pause className="mx-auto h-14 w-14 text-white/40" /> : seconds}
        </div>
        <div className="h-1.5 w-full max-w-md overflow-hidden rounded-full bg-white/10">
          <div className={cn('h-full transition-[width] duration-200', urgent ? 'bg-rose-400' : 'bg-sky-400')} style={{ width: `${fraction * 100}%` }} />
        </div>
        {state.phase === 'turn' && (
          <div className="flex flex-wrap justify-center gap-2">
            {running ? (
              <Btn tone="ghost" onClick={() => dispatch({ type: 'pause', now: Date.now() })}>
                <Pause className="h-4 w-4" />
                Fasilə
              </Btn>
            ) : (
              <Btn tone="ghost" onClick={() => dispatch({ type: 'resume', now: Date.now() })}>
                <Play className="h-4 w-4" />
                Davam et
              </Btn>
            )}
            <Btn tone="ghost" onClick={() => dispatch({ type: 'fail', side: active, round: round.no, reason: 'gaveUp', now: Date.now() })}>
              <Flag className="h-4 w-4" />
              Tapa bilmədik
            </Btn>
          </div>
        )}
      </Panel>

      {state.phase === 'turn' && (
        <form onSubmit={submit}>
          <Panel>
            <PanelTitle>{sideName(state, active)}, şəxsin adını yazın</PanelTitle>
            <div className="flex flex-col gap-4 p-4 sm:p-5">
              <Field
                label={mode === 'sound' ? 'Ad və soyad (tələffüz olunduğu kimi)' : 'Ad və soyad'}
                hint={mode === 'sound' ? 'Azərbaycan hərfləri ilə, səsləndiyi kimi: Jerar Depardye, Uill Smit.' : 'Öz dilində yazıldığı kimi: Gérard Depardieu.'}
              >
                <input className={cn(inputClass, 'text-lg')} value={fullName} onChange={e => setFullName(e.target.value)} autoFocus disabled={!running} maxLength={80} />
              </Field>

              {words.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {words.map((w, i) => (
                    <WordChip
                      key={`${w}-${i}`}
                      word={w}
                      pos={i === effective.nameIdx ? round.rule.name : i === effective.surnameIdx && words.length > 1 ? round.rule.surname : null}
                      ok={i === effective.nameIdx ? result.nameOk : result.surnameOk}
                      role={i === effective.nameIdx ? 'ad' : i === effective.surnameIdx && words.length > 1 ? 'soyad' : undefined}
                    />
                  ))}
                </div>
              )}

              {words.length > 2 && (
                <div className="grid gap-3 rounded-xl border border-white/5 p-3 sm:grid-cols-2">
                  <Field label="Ad hansı sözdür">
                    <Segmented value={effective.nameIdx} onChange={nameIdx => setParts({ ...effective, nameIdx })} options={wordOptions} />
                  </Field>
                  <Field label="Soyad hansı sözdür">
                    <Segmented value={effective.surnameIdx} onChange={surnameIdx => setParts({ ...effective, surnameIdx })} options={wordOptions} />
                  </Field>
                </div>
              )}

              {mode === 'sound' && (
                <Field label="Orijinal yazılış (istəyə görə)" hint="Rəqibin yoxlaması üçün: Gérard Depardieu.">
                  <input className={inputClass} value={original} onChange={e => setOriginal(e.target.value)} disabled={!running} maxLength={80} />
                </Field>
              )}

              <Field label="Kimdir? Onu haradan tanıyırsınız?" hint="Dəqiq olmasa da olar, amma bu şəxs haqqında nəsə bilməlisiniz.">
                <input
                  className={inputClass}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="məs. fransız aktyor, «Asteriks və Obeliks»"
                  disabled={!running}
                  maxLength={160}
                />
              </Field>

              {fullName.trim() && result.problems.length > 0 && (
                <ul className="flex flex-col gap-1 text-sm text-rose-300">
                  {result.problems.map(p => (
                    <li key={p} className="flex items-start gap-1.5">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              )}
              {result.ok && (
                <p className="flex items-center gap-1.5 text-sm text-emerald-300">
                  <CheckCircle2 className="h-4 w-4" />
                  Hərflər uyğundur. Rəqibin təsdiqinə göndərin.
                </p>
              )}
              {!running && <p className="text-sm text-amber-200">Taymer dayandırılıb — davam etmək üçün «Davam et» düyməsini basın.</p>}

              <Btn type="submit" className="self-end px-6 py-3" disabled={!result.ok || !running}>
                <Send className="h-4 w-4" />
                Təsdiqə göndər
              </Btn>
            </div>
          </Panel>
        </form>
      )}

      {state.phase === 'review' && state.pending && (
        <Panel>
          <PanelTitle>{sideName(state, opponent)}, cavabı yoxlayın</PanelTitle>
          <div className="flex flex-col gap-4 p-4 sm:p-5">
            <div>
              <p className="text-2xl font-black">{state.pending.fullName}</p>
              {state.pending.original && <p className="text-white/50">{state.pending.original}</p>}
              <p className="mt-2 text-white/80">«{state.pending.description}»</p>
            </div>
            <div className="flex flex-wrap gap-2 text-sm">
              {searchLinks(state.pending).map(link => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-white/70 hover:text-white"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  {link.label}
                </a>
              ))}
            </div>
            <ul className="list-disc pl-5 text-sm text-white/50">
              <li>Hərflər sistem tərəfindən yoxlanılıb.</li>
              <li>Bu şəxs həqiqətən var və təsvir ona uyğundur?</li>
              {mode === 'sound' && <li>Ad düzgün tələffüz olunduğu kimi yazılıb?</li>}
            </ul>
            <input
              className={inputClass}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Etiraz səbəbi (istəyə görə)"
              aria-label="Etiraz səbəbi"
              maxLength={160}
            />
            <div className="flex flex-wrap justify-end gap-2">
              <Btn tone="danger" onClick={() => dispatch({ type: 'review', accept: false, reason, now: Date.now() })}>
                <X className="h-4 w-4" />
                Etiraz edirəm
              </Btn>
              <Btn tone="success" onClick={() => dispatch({ type: 'review', accept: true, now: Date.now() })}>
                <Check className="h-4 w-4" />
                Qəbul edirəm
              </Btn>
            </div>
            <p className="text-xs text-white/40">Etiraz olunsa, növbə qalan vaxtla {sideName(state, active)} tərəfində qalır.</p>
          </div>
        </Panel>
      )}
    </div>
  );
};

export default InitialsTurn;
